import { useCallback, useMemo, useRef, useState, ReactNode } from 'react';

import { CheckIcon, ChevronDownIcon, PlusIcon, Trash2Icon } from 'lucide-react';
import { Control, Controller, FieldValues, Path, UseControllerProps } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { TruncatedText } from '@/components/atoms/TruncatedText/TruncatedText';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

// ============================================================================
// Types
// ============================================================================

export interface AutocompleteOption<TData = unknown> {
  value: string;
  label: string;
  category?: string;
  disabled?: boolean;
  data?: TData;
}

interface AutocompleteInnerProps<TData = unknown> {
  options: AutocompleteOption<TData>[];
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  label?: ReactNode | string;
  placeholder?: string;
  searchPlaceholder?: string;
  onCreateNew?: (value: string) => void;
  ariaLabel?: string;
  /** Placeholder text shown in "Add new" button when no search term is entered (e.g., "tag", "variable") */
  createNewPlaceholder?: string;
  /**
   * Override the "Add new" item label. Returning an empty string hides the
   * item — mirrors the MultiSelect API used by ToolkitActionReset.
   */
  createNewText?: (searchValue: string) => string;
  /**
   * Disable (but keep visible) the "Add new" item when this returns true —
   * used by ToolkitEmail when the typed input doesn't pass recipient validation.
   */
  createNewDisabled?: (searchValue: string) => boolean;
  /** Show "Add new" button even when options is empty (default: false) */
  showCreateOnEmpty?: boolean;
  /** Always show "Add new" button regardless of search state (default: false) */
  alwaysShowCreate?: boolean;
  /** Constrain dropdown width to exactly match trigger width (default: false) */
  matchTriggerWidth?: boolean;
  disabled?: boolean;
  error?: boolean;
  errorMessage?: string;
  className?: string;
  renderOption?: (option: AutocompleteOption<TData>, isSelected: boolean) => ReactNode;
  renderValue?: (option: AutocompleteOption<TData>) => ReactNode;
  /** Hides the default check indicator for selected items. Use when `renderOption` handles its own. */
  hideSelectedIndicator?: boolean;
  /** Also match the search term against `option.value`. Leave off when the value is an id. */
  searchValues?: boolean;
  /** When set, a selected value swaps the chevron for a button that clears it. */
  onClear?: () => void;
  /** Options pinned above the filtered list, exempt from the search filter (e.g. a "None" row). */
  pinnedOptions?: AutocompleteOption<TData>[];
  /** Focuses the closed trigger on mount — e.g. when arriving via a deep link. */
  autoFocus?: boolean;
  /** Set on the trigger, so a `FormControl` can link it to its label, description and message. */
  id?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
}

export interface FormAutocompleteProps<TFieldValues extends FieldValues> extends Omit<
  AutocompleteInnerProps,
  'value' | 'onBlur' | 'onChange'
> {
  name: Path<TFieldValues>;
  control: Control<TFieldValues>;
  rules?: UseControllerProps<TFieldValues>['rules'];
  onChange?: (value: string) => void;
}

// ============================================================================
// Trigger Component
// ============================================================================

const matchesTerm = (
  option: { label: string; value: string },
  term: string,
  searchValues: boolean
) =>
  option.label.toLowerCase().includes(term) ||
  (searchValues && option.value.toLowerCase().includes(term));

const AutocompleteTrigger = ({
  children,
  className,
  error,
  disabled,
  open,
  hideChevron,
  ref,
  ...props
}: React.ComponentProps<'button'> & { error?: boolean; open?: boolean; hideChevron?: boolean }) => {
  return (
    <button
      ref={ref}
      type="button"
      role="combobox"
      aria-expanded={open}
      aria-controls="autocomplete-list"
      className={cn(
        'flex w-full items-center justify-between rounded-md border border-border bg-background px-3 py-2.5 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        error && 'border-destructive',
        disabled && 'cursor-not-allowed opacity-50',
        // Keeps the label clear of the button overlaid where the chevron was.
        hideChevron && 'pr-9',
        className
      )}
      disabled={disabled}
      {...props}
    >
      <span className="truncate" data-truncation-target="">
        {children}
      </span>
      {!hideChevron && (
        <ChevronDownIcon
          className={cn(
            'ml-2 h-4 w-4 shrink-0 text-muted-foreground transition-transform',
            open && 'rotate-180'
          )}
        />
      )}
    </button>
  );
};

// ============================================================================
// Helper: Truncate long text
// ============================================================================

function truncateText(text: string, maxLength = 20): string {
  if (text.length <= maxLength) {
    return text;
  }
  return `${text.slice(0, maxLength)}...`;
}

// ============================================================================
// Inner Component (Controlled)
// ============================================================================

function AutocompleteInner<TData = unknown>({
  options,
  value,
  onChange,
  onBlur,
  label,
  placeholder,
  searchPlaceholder,
  onCreateNew,
  ariaLabel,
  createNewPlaceholder,
  createNewText,
  createNewDisabled,
  showCreateOnEmpty = false,
  alwaysShowCreate = false,
  matchTriggerWidth = false,
  disabled = false,
  error = false,
  errorMessage,
  className,
  renderOption,
  renderValue,
  hideSelectedIndicator = false,
  searchValues = false,
  onClear,
  pinnedOptions = [],
  autoFocus = false,
  id,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
}: AutocompleteInnerProps<TData>) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const defaultPlaceholder = placeholder || t('common.select_an_option');
  const defaultSearchPlaceholder = searchPlaceholder || t('common.search_dots');
  const defaultCreateNewPlaceholder = createNewPlaceholder || t('common.item');

  // Group options by category
  const groupedOptions = useMemo(() => {
    const groups: Record<string, AutocompleteOption<TData>[]> = {};
    const uncategorized: AutocompleteOption<TData>[] = [];

    options.forEach((option) => {
      if (option.category) {
        if (!groups[option.category]) {
          groups[option.category] = [];
        }
        groups[option.category].push(option);
      } else {
        uncategorized.push(option);
      }
    });

    return { groups, uncategorized };
  }, [options]);

  // Filter options based on search term
  const filteredOptions = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    if (!term) {
      return groupedOptions;
    }

    const filteredGroups: Record<string, AutocompleteOption<TData>[]> = {};
    const filteredUncategorized: AutocompleteOption<TData>[] = [];

    // Filter grouped options
    Object.entries(groupedOptions.groups).forEach(([category, items]) => {
      const filtered = items.filter((item) => matchesTerm(item, term, searchValues));
      if (filtered.length > 0) {
        filteredGroups[category] = filtered;
      }
    });

    // Filter uncategorized options
    groupedOptions.uncategorized.forEach((item) => {
      if (matchesTerm(item, term, searchValues)) {
        filteredUncategorized.push(item);
      }
    });

    return { groups: filteredGroups, uncategorized: filteredUncategorized };
  }, [groupedOptions, searchTerm, searchValues]);

  // Check if there are any results
  const hasResults = useMemo(() => {
    return (
      filteredOptions.uncategorized.length > 0 || Object.keys(filteredOptions.groups).length > 0
    );
  }, [filteredOptions]);

  // Evaluate the resolvers against the current search term so callers can
  // drive label/disabled state per-keystroke.
  const resolvedCreateNewText = useMemo(
    () => createNewText?.(searchTerm) ?? '',
    [createNewText, searchTerm]
  );
  const resolvedCreateNewDisabled = useMemo(
    () => createNewDisabled?.(searchTerm) ?? false,
    [createNewDisabled, searchTerm]
  );

  // Check if create action should be shown
  const showCreateAction = useMemo(() => {
    if (!onCreateNew) {
      return false;
    }
    // When createNewText is provided and resolves to empty, hide the action
    // entirely (mirrors MultiSelect's createNewText gating).
    if (createNewText && !resolvedCreateNewText) {
      return false;
    }
    if (alwaysShowCreate) {
      return true;
    }
    // Show "Add" option when:
    // 1. No options exist and showCreateOnEmpty is true
    // 2. User has typed something that doesn't match existing options
    if (options.length === 0 && showCreateOnEmpty) {
      return true;
    }
    if (!searchTerm.trim()) {
      return false;
    }
    // Check if exact match exists (case-insensitive)
    const normalizedSearch = searchTerm.toLowerCase().trim();
    return !options.some((opt) => opt.label.toLowerCase() === normalizedSearch);
  }, [
    onCreateNew,
    createNewText,
    resolvedCreateNewText,
    alwaysShowCreate,
    searchTerm,
    options,
    showCreateOnEmpty,
  ]);

  // Get selected option and label - also handle values not in the options list (newly created)
  const selectedOption = useMemo(
    () => options.find((opt) => opt.value === value),
    [options, value]
  );

  const selectedLabel = useMemo(() => {
    // If value exists but not found in options, display the value itself (for newly created entries)
    return selectedOption?.label || value || '';
  }, [selectedOption, value]);

  const showClear = Boolean(onClear) && Boolean(selectedLabel) && !disabled;

  // Handle selection
  const handleSelect = useCallback(
    (optionValue: string) => {
      onChange(optionValue);
      setOpen(false);
      setSearchTerm('');
    },
    [onChange]
  );

  // Handle create new
  const handleCreateNew = useCallback(() => {
    if (onCreateNew && searchTerm.trim()) {
      const newValue = searchTerm.trim();
      // First update the form value so it's selected
      onChange(newValue);
      // Then notify parent about the new entry (for any additional handling)
      onCreateNew(newValue);
      setOpen(false);
      setSearchTerm('');
    }
  }, [onCreateNew, onChange, searchTerm]);

  // Handle open change
  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      setOpen(isOpen);
      if (!isOpen) {
        setSearchTerm('');
        onBlur?.();
      }
    },
    [onBlur]
  );

  const getTriggerContent = () => {
    if (selectedOption) {
      return renderValue ? renderValue(selectedOption) : selectedLabel;
    }
    if (selectedLabel) {
      return selectedLabel;
    }
    return <span>{defaultPlaceholder}</span>;
  };

  const triggerRef = useRef<HTMLButtonElement>(null);
  const hasAutoFocusedRef = useRef(false);

  // Radix `asChild` remakes the ref each render — without the guard, focus is stolen mid-typing.
  const setTriggerRef = useCallback(
    (node: HTMLButtonElement | null) => {
      triggerRef.current = node;
      if (node && autoFocus && !hasAutoFocusedRef.current) {
        hasAutoFocusedRef.current = true;
        node.focus();
      }
    },
    [autoFocus]
  );

  // Clearing unmounts the button itself — hand focus to the trigger, not <body>.
  const handleClear = () => {
    onClear?.();
    triggerRef.current?.focus();
  };

  const trigger = (
    <PopoverTrigger asChild className="h-10">
      <AutocompleteTrigger
        ref={setTriggerRef}
        open={open}
        error={error}
        disabled={disabled}
        hideChevron={showClear}
        aria-label={ariaLabel}
        id={id}
        aria-describedby={ariaDescribedBy}
        aria-invalid={ariaInvalid}
      >
        {getTriggerContent()}
      </AutocompleteTrigger>
    </PopoverTrigger>
  );

  return (
    <div className={cn('w-full', className)}>
      {label && typeof label === 'string' && (
        <label className="mb-2 block text-xs font-semibold text-muted-foreground uppercase">
          {label}
        </label>
      )}

      {label && typeof label !== 'string' && label}

      <Popover modal open={open} onOpenChange={handleOpenChange}>
        {/* Branch on `onClear` (stable), not `showClear`: consumers that don't opt in
            keep their exact previous DOM, and the trigger never remounts on select/clear.
            `hideChevron` is always false without `onClear`, so `trigger` is shared. */}
        {onClear ? (
          // relative so the clear button can overlay the chevron's spot as a sibling
          // of the trigger — buttons can't nest.
          <div className="relative">
            {trigger}
            {showClear && (
              <button
                type="button"
                aria-label={t('common.clear')}
                onClick={handleClear}
                // The transparent border mirrors the trigger's own, so the icon lands
                // on the same pixel the chevron used.
                className="absolute inset-y-0 right-0 flex items-center rounded-md border border-transparent px-3 text-muted-foreground transition-colors hover:text-destructive focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
              >
                <Trash2Icon className="h-4 w-4" />
              </button>
            )}
          </div>
        ) : (
          trigger
        )}

        <PopoverContent
          className={cn(
            'z-1300 min-w-(--radix-popover-trigger-width) p-0',
            matchTriggerWidth && 'max-w-(--radix-popover-trigger-width)'
          )}
          align="start"
        >
          <Command shouldFilter={false}>
            <CommandInput
              placeholder={defaultSearchPlaceholder}
              value={searchTerm}
              onValueChange={setSearchTerm}
            />

            <CommandList id="autocomplete-list" className="scroll-py-1">
              {/* Pinned items (e.g. a "None"/clear row) stay visible above the
                  search-filtered list instead of being matched against searchTerm. */}
              {pinnedOptions.length > 0 && (
                <>
                  <CommandGroup>
                    {pinnedOptions.map((option) => (
                      <CommandItem
                        key={option.value || option.label}
                        value={option.value || option.label}
                        disabled={option.disabled}
                        onSelect={() => handleSelect(option.value)}
                        className="flex items-center justify-between"
                      >
                        {renderOption ? (
                          renderOption(option, value === option.value)
                        ) : (
                          <span>{option.label}</span>
                        )}
                        {!hideSelectedIndicator && value === option.value && (
                          <CheckIcon className="h-4 w-4 text-primary" />
                        )}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                  <CommandSeparator />
                </>
              )}

              {/* Uncategorized items first */}
              {filteredOptions.uncategorized.length > 0 && (
                <CommandGroup>
                  {filteredOptions.uncategorized.map((option) => (
                    <CommandItem
                      key={option.value || option.label}
                      value={option.value || option.label}
                      disabled={option.disabled}
                      onSelect={() => handleSelect(option.value)}
                      className="flex items-center justify-between"
                    >
                      {renderOption ? (
                        renderOption(option, value === option.value)
                      ) : (
                        <span>{option.label}</span>
                      )}
                      {!hideSelectedIndicator && value === option.value && (
                        <CheckIcon className="h-4 w-4 text-primary" />
                      )}
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}

              {/* Grouped items with sticky headers */}
              {Object.entries(filteredOptions.groups).map(([category, items]) => (
                <CommandGroup
                  key={category}
                  heading={
                    <TruncatedText text={category} side="right" size="large" className="block" />
                  }
                  className="**:[[cmdk-group-heading]]:sticky **:[[cmdk-group-heading]]:top-0 **:[[cmdk-group-heading]]:z-10"
                >
                  {items.map((option) => (
                    <CommandItem
                      key={option.value || option.label}
                      value={option.value || option.label}
                      disabled={option.disabled}
                      onSelect={() => handleSelect(option.value)}
                      className="flex items-center justify-between"
                    >
                      {renderOption ? (
                        renderOption(option, value === option.value)
                      ) : (
                        <span>{option.label}</span>
                      )}
                      {!hideSelectedIndicator && value === option.value && (
                        <CheckIcon className="h-4 w-4 text-primary" />
                      )}
                    </CommandItem>
                  ))}
                </CommandGroup>
              ))}

              {/* Empty state - only show when searching and no results */}
              {!hasResults && searchTerm && !showCreateAction && (
                <CommandEmpty>{t('common.no_results')}</CommandEmpty>
              )}
            </CommandList>

            {/* Create new footer - fixed at bottom */}
            {showCreateAction && (
              <div className="border-t p-1">
                <CommandItem
                  disabled={resolvedCreateNewDisabled}
                  onSelect={() => {
                    if (resolvedCreateNewDisabled) {
                      return;
                    }
                    handleCreateNew();
                  }}
                  className="flex items-center gap-2 text-foreground"
                >
                  <PlusIcon className="h-4 w-4" />
                  <span>
                    {resolvedCreateNewText ||
                      t('common.add_new', {
                        0: searchTerm.trim()
                          ? truncateText(searchTerm.trim())
                          : defaultCreateNewPlaceholder,
                      })}
                  </span>
                </CommandItem>
              </div>
            )}
          </Command>
        </PopoverContent>
      </Popover>

      {errorMessage && <p className="mt-1 text-sm text-warning">{errorMessage}</p>}
    </div>
  );
}

// ============================================================================
// Main Component (with react-hook-form integration)
// ============================================================================

export function FormAutocomplete<TFieldValues extends FieldValues>({
  name,
  control,
  rules,
  onChange: handleChange,
  ...props
}: FormAutocompleteProps<TFieldValues>) {
  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
        <AutocompleteInner
          {...props}
          value={value || ''}
          onChange={(newValue) => {
            // 1. Update React Hook Form
            onChange(newValue);

            if (handleChange) {
              // 2. Fire your custom logic
              handleChange(newValue);
            }
          }}
          onBlur={onBlur}
          error={props.error || !!error}
          errorMessage={props.errorMessage || error?.message}
        />
      )}
    />
  );
}

// ============================================================================
// Standalone version (without react-hook-form)
// ============================================================================

export function Autocomplete<TData = unknown>(props: AutocompleteInnerProps<TData>) {
  return <AutocompleteInner {...props} />;
}
